import * as React from 'react';

import RemindAPI from 'lib/RemindAPI';
import { Query } from 'react-apollo';
import EventTracking from 'lib/EventTracking';
import ToastActions from 'actions/ToastActions';
import AdminEngagementReport from 'admin/engagementReports/components/AdminEngagementReport';
import GET_ENGAGEMENT_REPORT_WEEKS from 'admin/engagementReports/queries/GetEngagementReportWeeks.gql';
import GET_ENGAGEMENT_REPORT_DOWNLOAD_URL from 'admin/engagementReports/queries/GetEngagementReportDownloadUrl.gql';
import {
  GetEngagementReportWeeks,
  GetEngagementReportWeeksVariables,
} from 'admin/engagementReports/queries/__generated__/GetEngagementReportWeeks';
import {
  GetEngagementReportDownloadUrl,
  GetEngagementReportDownloadUrlVariables,
} from 'admin/engagementReports/queries/__generated__/GetEngagementReportDownloadUrl';

type Props = {
  orgUuid: string;
  paid: boolean;
  eventPrefix: string;
};

type State = {
  week: string;
  submitDisabled: boolean;
  reportsAvailable: boolean,
};

export default class AdminEngagementReportContainer extends React.Component<Props, State> {
  state: State = {
    week: '',
    submitDisabled: false,
    reportsAvailable: false,
  };

  componentEventPrefix = `${this.props.eventPrefix}.admin_engagement_reports`;

  trackEvent = (key: string, properties: { [key: string]: any } = {}) => {
    EventTracking.trackKey(`${this.componentEventPrefix}.${key}`, properties);
  };

  selectWeek = (week: string) => {
    this.setState({ week });
    this.trackEvent('select_week', { week: this.state.week });
  };

  fetchDownloadUrl = async () => {
    const { orgUuid } = this.props;
    const { week } = this.state;

    this.trackEvent('select_report_week', { orgUuid, selected: week });
    this.setState({ submitDisabled: true });

    try {
      const { engagementReports } = await RemindAPI.execute<
        GetEngagementReportDownloadUrl,
        GetEngagementReportDownloadUrlVariables
      >({
        query: GET_ENGAGEMENT_REPORT_DOWNLOAD_URL,
        variables: { orgUuid, week },
      });

      if (engagementReports?.reportDownloadUrl?.found) {
        window.open(engagementReports.reportDownloadUrl.downloadUrl);
      } else {
        ToastActions.showErrorToast('Engagement report does not exist or has expired.');
      }
    } catch (err) {
      console.warn(err);
      console.error(
        `Engagement Report Actions - could not find engagement report download url for org=${orgUuid}`
      );
      ToastActions.showGQLError(err);
    }

    this.setState({ submitDisabled: false });
  };

  render() {
    const { orgUuid, paid } = this.props;

    return (
      <Query<GetEngagementReportWeeks, GetEngagementReportWeeksVariables>
        query={GET_ENGAGEMENT_REPORT_WEEKS}
        variables={{ orgUuid }}
        onCompleted={data => {
          const weeks = data?.engagementReports?.reportWeeks || [];

          if (weeks.length && this.state.week !== weeks[0]) {
            this.selectWeek(weeks[0]);
          }

          this.setState({ reportsAvailable: !!weeks.length })
        }}>
        {({ loading, data }) => {
          return (
            <AdminEngagementReport
              orgUuid={orgUuid}
              weeks={data?.engagementReports?.reportWeeks || []}
              selected={this.state.week}
              submitDisabled={loading || this.state.submitDisabled || !this.state.reportsAvailable || !paid}
              eventPrefix={this.componentEventPrefix}
              selectWeek={this.selectWeek}
              downloadEngagementReport={this.fetchDownloadUrl}
            />
          );
        }}
      </Query>
    );
  }
}
